import { Carousel } from "@faire/retailer-visitor-shared/components/Carousel/Carousel";
import { ProductImage } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductImage";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackSeoSuppliersPageBrandListProductTileClick } from "@faire/web-api--source/events/seo/click/suppliersPageBrandListProductTile";
import { trackSeoSuppliersPageBrandListShopAllCtaClick } from "@faire/web-api--source/events/seo/click/suppliersPageBrandListShopAllCta";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISearchBrandsV2Response } from "@faire/web-api--source/indigofair/data/ISearchBrandsV2Response";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

interface ProductsCarouselProps {
  brand: ISearchBrandsV2Response.IBrand;
  categoryPathIdentifier: string;
  className?: string;
}

export const ProductsCarousel: React.FC<ProductsCarouselProps> = ({
  brand,
  categoryPathIdentifier,
  className,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    product_token_to_product_name,
    image_tokens_to_product_tokens,
    product_images,
    token,
    name,
  } = brand;
  const { isMobile } = useViewport();
  const itemsPerSlide = useResponsiveValue({
    mobileAndAbove: 2,
    desktopAndAbove: 3,
  });
  const location = useLocation();

  if (!product_images?.length) {
    return null;
  }

  const getProductLinkProps = (productToken: string) => {
    const { productUrl: to, replace } =
      generateProductLinkPropsForLoggedOutRetailer({
        productToken,
        brandToken: brand.token,
        location,
      }) ?? { productUrl: faireBaseUrl() };
    const onClick = () =>
      trackSeoSuppliersPageBrandListProductTileClick(
        productToken,
        categoryPathIdentifier
      );
    return { to, replace, onClick };
  };

  const products: { token: string; name: string; image: IImage }[] =
    product_images
      .map((image) => {
        const imageToken = image.token;
        const token = image_tokens_to_product_tokens[imageToken ?? ""];
        const name = product_token_to_product_name[token ?? ""];
        if (!imageToken || !token || !name) {
          return undefined;
        }
        return { token, name, image };
      })
      .filter(isNotUndefined);

  const ctaTileData = (token: string | undefined) => {
    const brandUrl = !token ? undefined : brandPage(token);
    if (brandUrl) {
      return {
        text: (
          <StrictLocalMsg
            defaultMessage="Shop all"
            description={{
              text: "Button navigating to brand page with all products",
            }}
          />
        ),
        linkTo: brandUrl,
        onClick: () =>
          trackSeoSuppliersPageBrandListShopAllCtaClick(token ?? ""),
        alwaysVisible: true,
      };
    }
    return undefined;
  };

  return (
    <Carousel
      className={className}
      items={products}
      renderItem={(product) => (
        <ProductLink
          key={product.token}
          {...getProductLinkProps(product.token)}
        >
          <ProductImage
            image={product.image}
            imageDimension={{
              mobileAndAbove: 100,
              tabletAndAbove: 150,
            }}
            alt={strictLocalize(
              {
                defaultMessage:
                  "Sign up to buy wholesale {ProductName} by {brandCompanyName}.",
                description: {
                  text: "product tile in brand results list on the suppliers page. click on the product tile will open the signup modal",
                  img: "https://cdn.faire.com/fastly/296ec600eadd5e6e9d159e52d17147d07a21990b8b38193b5aa617916fe806a5.png",
                },
              },
              { ProductName: product.name, brandCompanyName: name ?? "" }
            )}
          />
        </ProductLink>
      )}
      itemsPerSlide={itemsPerSlide}
      itemGap={8}
      peekNext={isMobile}
      peekGapPercent={20}
      lateralPadding={16}
      padItems
      noWrap
      ctaTileData={ctaTileData(token)}
    />
  );
};

const ProductLink = styled(Link)`
  display: flex;
  align-items: center;
`;
